import { useCallback } from "react";

import { SiteDTO } from "api";

import { QUERY_PARAMS_KEYS } from "consts";

import { sortBy } from "utils";

import { useMultiselectWithFetch } from "./multiselect";
import { OptionalMultiselectProps } from "./types";

import { useSites } from "../api";

/**
 * A custom hook for managing a multiselect component for sites, with optional filtering by business units.
 * @param props - Optional props for query params and external state updates.
 * @param businessUnits - Optional array of business units to filter sites.
 * @returns An object containing loading state, selected sites, options, and site IDs.
 */
export const useSitesMultiselect = ({
  updateExternalStates,
  getQueryParamsWithExternalChanges,
  withQueryParams = true,
}: OptionalMultiselectProps = {},
  businessUnits?: { name: string, value: string }[]) => {
  const getOptionsFromData = useCallback((sitesData?: SiteDTO[]) => {
    if (!sitesData?.length) return [];

    const allowedUnits = businessUnits?.map(({ value }) => value) || [];
    const filteredSites = allowedUnits.length > 0
      ? sitesData.filter((site) => allowedUnits.includes(site.businessUnit ?? ''))
      : sitesData;

    const sortedSites = sortBy({ array: filteredSites, key: "name" });

    return sortedSites.map((site) => ({
      name: site.name || `Site: ${site.id}`,
      label: site.name || `Site: ${site.id}`,
      value: site.id,
    }));
  }, [businessUnits]);

  const {
    isLoading: isSitesLoading,
    setSelectedOptions: setSelectedSites,
    selectedOptions: selectedSites,
    options: sitesOptions,
    selectedOptionValues: siteIds,
  } = useMultiselectWithFetch({
    updateExternalStates,
    getQueryParamsWithExternalChanges,
    withQueryParams,
    useGetData: useSites,
    getOptionsFromData,
    queryKey: QUERY_PARAMS_KEYS.SITE_IDS,
  });

  return {
    isSitesLoading,
    setSelectedSites,
    sitesOptions,
    selectedSites,
    siteIds: siteIds as number[],
  };
};
